// src/utils/ticketCache.js
// ─────────────────────────────────────────────────────────────────────────────
// An offline copy of the customer's ticket, in the browser.
//
// The problem this closes (audit P1-11): with 400 people on one cell tower at
// doors-open, `apiGetTicket` is a bare network call and nobody can open a
// ticket. The gate stops. The web client has no service worker at all.
//
// SCOPE, stated plainly: a real service worker — precached shell, offline
// navigation, background sync — is out of scope here. This is the equivalent
// done with storage the app already has (localStorage, which redux-persist
// already relies on). It means an already-loaded tab, or a return visit whose
// HTML/JS came from the browser's own HTTP cache, can still render the ticket
// with no network. A genuinely cold load on a dead connection still cannot boot
// the app; only a service worker fixes that, and it should be a separate piece
// of work.
//
// On caching the token at all: the code comment being relaxed here said it
// "must never be cached/persisted on the device". The trade-off changed.
// TOKEN_MAX_AGE_MS is 48 hours, so the token is signature-valid for 48 hours
// whether or not it touches disk — a screenshot concedes exactly the same, and
// every customer takes one. What actually stops a replayed token is the atomic
// status re-check at redemption, which is unaffected. 48h is therefore also the
// natural TTL: past it the cached token would fail the server's age check
// anyway, so a stale entry is worse than none and is dropped on read.
// ─────────────────────────────────────────────────────────────────────────────

import { forgetLiveKey } from "./liveTicketStore";

/** Matches the server's TOKEN_MAX_AGE_MS (ticketCredentialService.js). */
export const TICKET_CACHE_TTL_MS = 48 * 60 * 60 * 1000;

const SNAPSHOT_PREFIX = "lusuku:ticket:v1:";
const INDEX_KEY = "lusuku:tickets:index:v1";

const snapshotKey = (ticketId) => `${SNAPSHOT_PREFIX}${String(ticketId)}`;

// Safari in private mode throws on every localStorage access, and a quota-full
// origin throws on write. Neither may take the ticket screen down with it.
const readRaw = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeRaw = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};
const removeRaw = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* no-op */
  }
};

/** Keep only what the ticket page renders. */
const slimTicket = (t) => {
  if (!t) return null;
  return {
    _id: t._id ? String(t._id) : null,
    serial: t.serial || null,
    ticketTypeName: t.ticketTypeName || null,
    seatLabel: t.seatLabel || null,
    unitLabel: t.unitLabel || null,
    admissionsCount: Number(t.admissionsCount || 1),
    price: t.price === undefined ? null : t.price,
    currency: t.currency || null,
    status: t.status || null,
    displayStatus: t.displayStatus || null,
    entryPolicy: t.entryPolicy || null,
    redeemedAt: t.redeemedAt || null,
    showingId: t.showingId ? String(t.showingId) : null,
    eventId: t.eventId ? String(t.eventId) : null,
    qrToken: t.qrToken || null,
    // Live tickets (16 Oct 2026): no static QR. The display key is kept by
    // liveTicketStore.js, not here; these say where the ticket is shown.
    liveHeldElsewhere: Boolean(t.liveHeldElsewhere),
    liveRequired: Boolean(t.liveRequired),
  };
};

const slimSnapshot = (payload) => ({
  ticket: slimTicket(payload && payload.ticket),
  event: payload && payload.event
    ? { title: payload.event.title || null, venueName: payload.event.venueName || null }
    : null,
  showing: payload && payload.showing
    ? {
        _id: payload.showing._id ? String(payload.showing._id) : null,
        startsAt: payload.showing.startsAt || null,
        status: payload.showing.status || null,
      }
    : null,
  venue: payload && payload.venue ? { name: payload.venue.name || null } : null,
});

const isFresh = (savedAt) =>
  typeof savedAt === "number" && Date.now() - savedAt < TICKET_CACHE_TTL_MS;

/** Persist the last-issued token plus serial, seat, showtime and venue. */
export const saveTicketSnapshot = (ticketId, payload) => {
  if (!ticketId || !payload || !payload.ticket) return false;
  return writeRaw(
    snapshotKey(ticketId),
    JSON.stringify({ savedAt: Date.now(), data: slimSnapshot(payload) })
  );
};

/** → `{ savedAt, data }`, or null when absent, unparseable or past the TTL. */
export const loadTicketSnapshot = (ticketId) => {
  if (!ticketId) return null;
  const raw = readRaw(snapshotKey(ticketId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.data.ticket || !isFresh(parsed.savedAt)) {
      removeRaw(snapshotKey(ticketId));
      return null;
    }
    return { savedAt: parsed.savedAt, data: parsed.data };
  } catch (e) {
    removeRaw(snapshotKey(ticketId));
    return null;
  }
};

/**
 * Seed the per-ticket cache from a `/my-tickets` page.
 *
 * `getMyTickets` serialises every row through the same `serializeTicket` the
 * single-ticket endpoint uses, so the list already carries a freshly minted
 * qrToken for every VALID ticket. Loading the list once therefore makes every
 * ticket openable offline, not only the ones already visited.
 */
export const saveTicketsIndex = (tickets) => {
  const rows = Array.isArray(tickets) ? tickets : [];
  const index = rows.map((t) => ({
    _id: String(t._id),
    serial: t.serial || null,
    ticketTypeName: t.ticketTypeName || null,
    seatLabel: t.seatLabel || null,
    status: t.status || null,
    displayStatus: t.displayStatus || null,
    eventTitle: (t.event && t.event.title) || null,
    venueName: (t.event && t.event.venueName) || null,
    startsAt: (t.showing && t.showing.startsAt) || null,
  }));
  writeRaw(INDEX_KEY, JSON.stringify({ savedAt: Date.now(), index }));
  rows.forEach((t) => {
    if (!t || !t._id) return;
    // A ticket that can no longer be used keeps no live key here.
    if (t.status && t.status !== "VALID") forgetLiveKey(t._id);
    saveTicketSnapshot(t._id, {
      ticket: t,
      event: t.event || null,
      showing: t.showing || null,
      venue: t.event && t.event.venueName ? { name: t.event.venueName } : null,
    });
  });
  return index;
};

/** → `{ savedAt, index }` or null. Same 48h TTL. */
export const loadTicketsIndex = () => {
  const raw = readRaw(INDEX_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.index) || !isFresh(parsed.savedAt)) return null;
    return { savedAt: parsed.savedAt, index: parsed.index };
  } catch (e) {
    return null;
  }
};

const ticketCache = {
  TICKET_CACHE_TTL_MS,
  saveTicketSnapshot,
  loadTicketSnapshot,
  saveTicketsIndex,
  loadTicketsIndex,
};

export default ticketCache;
